import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useApp } from "@/contexts/AppContext";
import { Printer, Plug, PlugZap, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
    isWebUSBSupported,
    connectPrinter,
    disconnectPrinter,
    autoReconnect,
    isPrinterConnected,
    getPrinterInfo,
    testPrint,
} from "@/lib/escpos";

export default function PrinterStatusCard({ compact = false }) {
    const { t } = useApp();
    const [connected, setConnected] = useState(false);
    const [info, setInfo] = useState(null);
    const [supported, setSupported] = useState(true);
    const [busy, setBusy] = useState(false);

    const refresh = () => {
        setConnected(isPrinterConnected());
        setInfo(getPrinterInfo());
    };

    useEffect(() => {
        const s = isWebUSBSupported();
        setSupported(s);
        if (s) {
            autoReconnect().then(refresh);
        }
    }, []);

    const onConnect = async () => {
        setBusy(true);
        try {
            await connectPrinter();
            refresh();
            toast.success(t("printer_connected"));
        } catch (e) {
            const msg = String(e?.message || e);
            if (!msg.includes("No device selected")) toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const onDisconnect = async () => {
        setBusy(true);
        await disconnectPrinter();
        refresh();
        setBusy(false);
    };

    const onTest = async () => {
        setBusy(true);
        try {
            await testPrint();
            toast.success(t("printer_test_sent"));
        } catch (e) {
            toast.error(String(e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    if (!supported) {
        return (
            <Card className="grain bg-card/70 border-destructive/30" data-testid="printer-card">
                <CardContent className="p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-medium">{t("printer")}</div>
                        <div className="text-muted-foreground text-xs mt-1">{t("printer_unsupported")}</div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (compact) {
        // small inline status pill for cashier home top-bar
        return (
            <div className="flex items-center gap-2" data-testid="printer-compact">
                <div className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-500 glow-pulse" : "bg-muted-foreground/50"}`} />
                <span className="text-xs text-muted-foreground">
                    {connected ? t("printer_connected") : t("printer_not_connected")}
                </span>
                {!connected && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onConnect} disabled={busy} data-testid="printer-connect-compact">
                        <Plug className="h-3 w-3 me-1" />{t("connect_printer")}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <Card className="grain bg-card/70" data-testid="printer-card">
            <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className={`rounded-xl p-2.5 ${connected ? "bg-emerald-500/15 text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                            <Printer className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-medium">{t("printer")}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                {connected ? (info?.productName || "USB Printer") : t("printer_not_connected")}
                            </div>
                            {connected && info && (
                                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                                    VID: {info.vendorId?.toString(16).padStart(4, "0").toUpperCase()} · PID: {info.productId?.toString(16).padStart(4, "0").toUpperCase()}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`h-2.5 w-2.5 rounded-full ${connected ? "bg-emerald-500 glow-pulse" : "bg-muted-foreground/40"}`} />
                </div>
                <div className="flex gap-2 pt-1">
                    {!connected ? (
                        <Button onClick={onConnect} disabled={busy} className="flex-1" data-testid="connect-printer-btn">
                            <Plug className="h-4 w-4 me-2" />{t("connect_printer")}
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onTest} disabled={busy} className="flex-1" data-testid="test-print-btn">
                                <PlugZap className="h-4 w-4 me-2" />{t("test_print")}
                            </Button>
                            <Button variant="ghost" onClick={onDisconnect} disabled={busy} data-testid="disconnect-printer-btn">
                                {t("disconnect_printer")}
                            </Button>
                        </>
                    )}
                </div>
                <div className="text-xs text-muted-foreground border-t border-border pt-3">
                    💡 Connect once per device. Browser remembers it next time. Works on Chrome — Windows PC or Android tablet (with USB-OTG cable).
                </div>
            </CardContent>
        </Card>
    );
}
